import { PageWrapper, useLayout } from '@approvalmax/ui/src/components';
import { selectors } from 'modules/common';
import { hocs as pageHocs } from 'modules/page';
import { useDispatch, useSelector } from 'modules/react-redux';
import { memo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useUnmount } from 'react-use';
import { amplitudeService } from 'services/amplitude';
import { HelpStripe, useBackHeader } from 'shared/components';
import { getPath, Path } from 'urlBuilder';

import { pageConfig } from './config';
import Loading from './loading';
import { initializeModule, leavePage } from './resources/actions';
import { Popups, VersionHistory, WorkflowDetails } from './resources/components';
import Toolbar from './resources/components/Toolbar/Toolbar';
import { useBreadcrumbs } from './resources/hooks/useBreadcrumbs';
import { useWorkflowPageData } from './resources/hooks/useWorkflowPageData';
import { messages } from './resources/messages';
import { getActiveTemplate } from './resources/selectors/pageSelectors';
import { createHelpItemId, WorkflowHelpItemType } from './resources/utils/helpItems';

export const Content = memo(() => {
    const dispatch = useDispatch();
    const company = useSelector(selectors.navigation.getActiveCompany);
    const template = useSelector(getActiveTemplate);
    const helpItemId = template ? createHelpItemId(WorkflowHelpItemType.Card, template.integrationCode) : '';

    useEffect(() => {
        dispatch(initializeModule());
    }, [dispatch]);

    useBreadcrumbs();

    useEffect(() => {
        if (template) {
            amplitudeService.sendData('workflow: view workflow', {
                workflow: template.integrationCode?.toLocaleLowerCase() || 'standalone',
            });
        }
    }, [template]);

    useUnmount(() => {
        dispatch(leavePage());
    });

    useBackHeader(getPath(Path.companyWorkflows, company.id), true);

    return (
        <PageWrapper>
            <Helmet
                title={messages.title({
                    templateName: template?.displayName || messages.unnamed,
                    companyName: company.displayName,
                })}
            />

            <PageWrapper.Toolbar>
                <Toolbar helpStripeId={helpItemId} />

                <HelpStripe id={helpItemId} spacingBottom={0} />
            </PageWrapper.Toolbar>

            <PageWrapper.Content spacingTop={32}>
                <WorkflowDetails />
            </PageWrapper.Content>

            <VersionHistory helpStripeId={helpItemId} />

            <Popups />
        </PageWrapper>
    );
});

const Page = memo(() => {
    const innerActivePageId = useSelector(selectors.navigation.getInnerActivePageId);
    const template = useSelector(getActiveTemplate);

    useLayout({ hasHeader: true, hasDrawer: true });

    const { isInitialLoading } = useWorkflowPageData();

    if (isInitialLoading || innerActivePageId !== pageConfig.innerActivePageId || !template) {
        return <Loading />;
    }

    return <Content />;
});

export default pageHocs.withProtected({ Page, roles: pageConfig.access });
